// Junior Science Student Notes - Years 7-10
// Comprehensive study materials for Basic Science

export const year7ScienceStudentNotes = {
  year: 7,
  subject: "Basic Science",
  strands: {
    strand1: {
      name: "Measurement & Physical Science",
      units: [
        {
          id: "7.1",
          name: "Measurement",
          objectives: [
            "Understand conservation of length, area, volume, and mass",
            "Use appropriate measuring instruments correctly",
            "Convert between metric units",
            "Estimate measurements with reasonable accuracy"
          ],
          keyTerms: [
            { term: "Length", definition: "The distance between two points, measured in mm, cm, m, or km" },
            { term: "Area", definition: "The amount of surface covered, measured in square units (cm², m²)" },
            { term: "Volume", definition: "The amount of space an object occupies, measured in cm³, ml, or L" },
            { term: "Mass", definition: "The amount of matter in an object, measured in g or kg" },
            { term: "Conservation", definition: "The principle that a quantity remains the same even when the object changes shape" },
            { term: "Meniscus", definition: "The curved surface of liquid in a measuring cylinder - read at the bottom of the curve" }
          ],
          studyNotes: `## Measurement in Science

### Why Measurement is Important
Scientists need accurate measurements to:
- Compare results with other scientists
- Repeat experiments reliably
- Make predictions based on data

### Measuring Instruments
| Quantity | Instrument | Units |
|----------|------------|-------|
| Length | Ruler, tape measure | mm, cm, m |
| Volume | Measuring cylinder | ml, L |
| Mass | Balance (scale) | g, kg |
| Time | Stop clock | seconds, minutes |
| Temperature | Thermometer | °C |

### Converting Units
**Length:** 1 km = 1000 m, 1 m = 100 cm, 1 cm = 10 mm
**Volume:** 1 L = 1000 ml, 1 ml = 1 cm³
**Mass:** 1 kg = 1000 g

### Estimating
Before measuring, always estimate first. This helps you:
- Choose the right instrument
- Know if your measurement makes sense
- Spot errors in your work`,
          practiceQuestions: [
            "A desk is 1.2 m long. How many centimeters is this?",
            "Convert 2500 ml to liters",
            "Which instrument would you use to measure the volume of a small rock?",
            "Why should you read a measuring cylinder at eye level?",
            "A student measures a book as 21.5 cm. Is this length, area, or volume?"
          ],
          examTips: "Always include units in your answers. Show your working when converting units."
        },
        {
          id: "7.4",
          name: "Introduction to Magnetism and Electricity",
          objectives: [
            "Understand properties of magnets and magnetic materials",
            "Know how to use a compass for direction finding",
            "Build and draw simple electrical circuits",
            "Identify conductors and insulators"
          ],
          keyTerms: [
            { term: "Magnet", definition: "An object that attracts iron and steel materials" },
            { term: "Magnetic poles", definition: "The ends of a magnet where the magnetic force is strongest (North and South)" },
            { term: "Magnetic field", definition: "The area around a magnet where magnetic force can be detected" },
            { term: "Conductor", definition: "A material that allows electricity to flow through it easily (e.g., metals)" },
            { term: "Insulator", definition: "A material that does not allow electricity to flow through it (e.g., plastic, rubber)" },
            { term: "Circuit", definition: "A complete path through which electricity can flow" },
            { term: "Current", definition: "The flow of electrons through a conductor" }
          ],
          studyNotes: `## Magnetism

### Properties of Magnets
- Magnets attract iron and steel (magnetic materials)
- Every magnet has two poles: North (N) and South (S)
- Like poles repel (N-N, S-S)
- Unlike poles attract (N-S)
- Magnetic force is strongest at the poles

### Compass
- Contains a small magnet that points North-South
- Used for navigation and direction finding
- Earth acts like a giant magnet

## Electricity

### Simple Circuits
For electricity to flow, you need:
1. A power source (battery/cell)
2. A complete conducting path (wires)
3. A component (bulb, motor)

### Circuit Symbols
- Cell: ─|├─
- Battery: ─|├|├─
- Bulb: ─⊗─
- Switch (open): ─○ ○─
- Switch (closed): ─○─○─

### Safety
⚠️ Domestic electricity (240V) is DANGEROUS
- Never touch exposed wires
- Never use electrical items near water
- Only use low-voltage batteries in experiments`,
          practiceQuestions: [
            "What happens when two north poles are brought together?",
            "Name three magnetic materials",
            "Why does a compass needle point north?",
            "Draw a circuit with one cell, one switch, and one bulb",
            "Why are wires covered with plastic?"
          ],
          examTips: "Practice drawing circuit diagrams using correct symbols. Remember: a circuit must be complete for current to flow."
        }
      ]
    },
    strand2: {
      name: "Water & Materials",
      units: [
        {
          id: "7.2",
          name: "Water",
          objectives: [
            "Understand the three states of water and how to change between them",
            "Explain evaporation, condensation, freezing, and melting",
            "Separate mixtures using filtration and evaporation",
            "Describe the water cycle and its importance"
          ],
          keyTerms: [
            { term: "Evaporation", definition: "The change from liquid to gas at temperatures below boiling point" },
            { term: "Condensation", definition: "The change from gas to liquid when cooled" },
            { term: "Freezing", definition: "The change from liquid to solid at 0°C for water" },
            { term: "Melting", definition: "The change from solid to liquid" },
            { term: "Boiling", definition: "Rapid change from liquid to gas at 100°C for water" },
            { term: "Solution", definition: "A mixture where a solid (solute) dissolves in a liquid (solvent)" },
            { term: "Filtration", definition: "Separating an insoluble solid from a liquid using filter paper" }
          ],
          studyNotes: `## States of Water

### Three States
| State | Particle Arrangement | Properties |
|-------|---------------------|------------|
| Solid (ice) | Fixed, close together | Fixed shape, hard |
| Liquid (water) | Close but can move | Flows, takes container shape |
| Gas (steam) | Far apart, moving fast | Fills any space, invisible |

### Changing States
- **Melting** (solid → liquid): Add heat (0°C for ice)
- **Freezing** (liquid → solid): Remove heat (0°C for water)
- **Evaporation** (liquid → gas): Add heat (any temperature)
- **Boiling** (liquid → gas): Add heat (100°C for water)
- **Condensation** (gas → liquid): Remove heat

### The Water Cycle
1. Sun heats water in oceans, lakes, rivers (evaporation)
2. Water vapour rises and cools (condensation forms clouds)
3. Water falls as rain (precipitation)
4. Water flows back to sea (runoff)

### Separating Mixtures
- **Filtration**: Sand from water (insoluble solid)
- **Evaporation**: Salt from salt water (dissolved solid)
- **Distillation**: Pure water from salt water`,
          practiceQuestions: [
            "Why does evaporation cause cooling?",
            "Explain why wet clothes dry on a clothesline",
            "How would you separate sand and salt mixed together?",
            "Draw and label the water cycle",
            "Why does water pressure increase with depth?"
          ],
          examTips: "Be able to explain state changes in terms of particles gaining or losing energy."
        }
      ]
    },
    strand3: {
      name: "Living Things",
      units: [
        {
          id: "7.3",
          name: "Observation of Living Things",
          objectives: [
            "Observe and describe features of living organisms",
            "Use simple classification keys",
            "Understand adaptation and food chains",
            "Use scientific vocabulary for describing organisms"
          ],
          keyTerms: [
            { term: "Adaptation", definition: "A feature that helps an organism survive in its environment" },
            { term: "Food chain", definition: "A diagram showing how energy passes from one organism to another" },
            { term: "Producer", definition: "An organism that makes its own food (plants)" },
            { term: "Consumer", definition: "An organism that eats other organisms for food" },
            { term: "Predator", definition: "An animal that hunts and eats other animals" },
            { term: "Prey", definition: "An animal that is hunted and eaten by predators" },
            { term: "Habitat", definition: "The place where an organism lives" }
          ],
          studyNotes: `## Observing Living Things

### Using a Hand Lens
1. Hold the lens close to your eye
2. Move the object until it's in focus
3. Draw what you see with labels

### Classification Keys
A key helps identify organisms by asking questions:
- Does it have wings? → Yes/No
- Does it have 6 legs? → Yes/No

### Adaptations
Examples of adaptations in Vanuatu:
- **Coconut palm**: Flexible trunk (survives cyclones)
- **Flying fox**: Large eyes (night vision)
- **Reef fish**: Bright colors (camouflage in coral)

### Food Chains
Example: Leaf → Caterpillar → Bird → Hawk

Rules:
- Arrows show direction of energy flow
- Always start with a producer (plant)
- End with a top predator`,
          practiceQuestions: [
            "Name three adaptations of a coconut palm",
            "Write a food chain found in a Vanuatu garden",
            "What is the difference between a producer and consumer?",
            "Why do we use scientific names for organisms?",
            "Create a simple key to identify three local animals"
          ],
          examTips: "Always draw arrows in food chains pointing from what is eaten to what eats it."
        },
        {
          id: "7.5",
          name: "Reproduction, Growth and Development",
          objectives: [
            "Know that living things are made of cells",
            "Understand the basic structure of cells",
            "Compare plant and animal cells",
            "Understand reproduction in plants and animals"
          ],
          keyTerms: [
            { term: "Cell", definition: "The basic unit of all living things" },
            { term: "Cell membrane", definition: "The outer boundary of a cell that controls what enters and leaves" },
            { term: "Cytoplasm", definition: "The jelly-like substance inside a cell where chemical reactions occur" },
            { term: "Nucleus", definition: "The control center of the cell containing genetic information" },
            { term: "Fertilization", definition: "The joining of male and female sex cells (gametes)" },
            { term: "Gamete", definition: "A sex cell (sperm or egg)" },
            { term: "Zygote", definition: "A fertilized egg cell" }
          ],
          studyNotes: `## Cells

### Cell Structure
All cells have:
- **Cell membrane**: Controls entry/exit of substances
- **Cytoplasm**: Where reactions happen
- **Nucleus**: Contains DNA, controls cell

### Plant vs Animal Cells
| Feature | Plant Cell | Animal Cell |
|---------|------------|-------------|
| Cell wall | Yes (rigid) | No |
| Chloroplasts | Yes | No |
| Large vacuole | Yes | Small/none |
| Shape | Regular (box-like) | Irregular |

## Reproduction

### Fertilization
- **External** (frogs): Eggs and sperm meet outside body (in water)
- **Internal** (mammals): Sperm meets egg inside female's body

### Plant Reproduction
1. Pollen lands on stigma (pollination)
2. Pollen tube grows to ovule
3. Male cell fertilizes egg cell
4. Seed develops

### Life Cycles
Frog: Egg → Tadpole → Froglet → Adult frog
This change is called **metamorphosis**`,
          practiceQuestions: [
            "Draw and label a plant cell",
            "What is the function of the nucleus?",
            "Why do plant cells have cell walls but animal cells don't?",
            "Explain the difference between internal and external fertilization",
            "Describe the life cycle of a frog"
          ],
          examTips: "When drawing cells, always include labels and a title. Use a ruler for straight lines."
        }
      ]
    }
  }
};

export const year8ScienceStudentNotes = {
  year: 8,
  subject: "Basic Science",
  strands: {
    strand1: {
      name: "Energy & Waves",
      units: [
        {
          id: "8.1",
          name: "Heat and Air",
          objectives: [
            "Understand heat transfer by conduction, convection, and radiation",
            "Explain thermal expansion and contraction",
            "Know the composition of air",
            "Understand combustion and respiration"
          ],
          keyTerms: [
            { term: "Conduction", definition: "Heat transfer through solids by particles vibrating and passing energy to neighbors" },
            { term: "Convection", definition: "Heat transfer in fluids by hot material rising and cold material sinking" },
            { term: "Radiation", definition: "Heat transfer by electromagnetic waves (doesn't need particles)" },
            { term: "Thermal expansion", definition: "Materials get bigger when heated as particles move more and spread out" },
            { term: "Combustion", definition: "Burning - a chemical reaction that needs fuel, oxygen, and heat" },
            { term: "Respiration", definition: "The process by which cells release energy from food using oxygen" }
          ],
          studyNotes: `## Heat Transfer

### Three Methods
1. **Conduction** (mainly in solids)
   - Particles vibrate and pass energy along
   - Metals are good conductors (fast transfer)
   - Plastic, wood are insulators (slow transfer)

2. **Convection** (in liquids and gases)
   - Hot fluid rises (less dense)
   - Cold fluid sinks (more dense)
   - Creates convection currents

3. **Radiation** (through space)
   - Doesn't need particles
   - Dark surfaces absorb/emit more
   - Shiny surfaces reflect radiation

### Expansion
- All materials expand when heated
- Used in thermometers, bimetallic strips
- Must be considered in construction (expansion gaps)

### Air Composition
| Gas | Percentage |
|-----|------------|
| Nitrogen | 78% |
| Oxygen | 21% |
| Other (CO₂, etc.) | 1% |

### Fire Triangle
Fire needs three things:
- **Fuel** (wood, petrol, gas)
- **Oxygen** (from air)
- **Heat** (to start the reaction)
Remove any one and fire goes out.`,
          practiceQuestions: [
            "Why are saucepans made of metal but have plastic handles?",
            "Explain how a convection current forms in a room with a heater",
            "Why do railway tracks have small gaps between sections?",
            "What percentage of air is oxygen?",
            "How would you put out an oil fire? Explain using the fire triangle."
          ],
          examTips: "Know the differences between the three methods of heat transfer and give examples of each."
        },
        {
          id: "8.2",
          name: "Light",
          objectives: [
            "Understand that light travels in straight lines",
            "Explain reflection and refraction",
            "Know how lenses form images",
            "Understand how the human eye works"
          ],
          keyTerms: [
            { term: "Reflection", definition: "Light bouncing off a surface - angle of incidence equals angle of reflection" },
            { term: "Refraction", definition: "Light bending when it passes from one medium to another" },
            { term: "Convex lens", definition: "A lens that is thicker in the middle and converges (focuses) light" },
            { term: "Concave lens", definition: "A lens that is thinner in the middle and diverges (spreads) light" },
            { term: "Spectrum", definition: "The band of colors seen when white light is split (ROYGBIV)" },
            { term: "Focal length", definition: "The distance from a lens to the point where parallel light rays meet" }
          ],
          studyNotes: `## Properties of Light

### Light Travels in Straight Lines
- Evidence: shadows have sharp edges
- Light cannot bend around corners
- This is why we can't see around obstacles

### Reflection
- **Law of reflection**: Angle of incidence = Angle of reflection
- Smooth surfaces give clear reflections (mirrors)
- Rough surfaces scatter light (paper)

### Refraction
- Light bends when entering different materials
- Bends TOWARDS normal when slowing down (air→water)
- Bends AWAY from normal when speeding up (water→air)
- This is why objects look bent in water

### Lenses
| Type | Shape | Effect | Uses |
|------|-------|--------|------|
| Convex | Thick middle | Converges light | Magnifying glass, eye |
| Concave | Thin middle | Diverges light | Short sight correction |

### The Human Eye
- **Cornea**: Bends light into eye
- **Pupil**: Controls light entry
- **Lens**: Fine focuses light
- **Retina**: Detects light (like film)
- **Optic nerve**: Sends signals to brain`,
          practiceQuestions: [
            "Why do shadows have sharp edges?",
            "Draw a ray diagram showing reflection off a plane mirror",
            "Why does a straw look bent in a glass of water?",
            "What type of lens is used in a magnifying glass?",
            "What causes short sight and how is it corrected?"
          ],
          examTips: "Always use a ruler when drawing ray diagrams. Mark angles and normal lines clearly."
        },
        {
          id: "8.3",
          name: "Sound",
          objectives: [
            "Understand that sound is caused by vibrations",
            "Know that sound needs a medium to travel",
            "Relate pitch to frequency and loudness to amplitude",
            "Understand how we hear sounds"
          ],
          keyTerms: [
            { term: "Vibration", definition: "A rapid back-and-forth movement that produces sound" },
            { term: "Frequency", definition: "The number of vibrations per second, measured in Hertz (Hz)" },
            { term: "Pitch", definition: "How high or low a sound is - depends on frequency" },
            { term: "Amplitude", definition: "The size of vibration - determines loudness" },
            { term: "Echo", definition: "A reflected sound wave heard after the original sound" },
            { term: "Medium", definition: "A material through which sound can travel (solid, liquid, gas)" }
          ],
          studyNotes: `## Sound

### Sound and Vibrations
- All sounds are made by vibrating objects
- Voice: vocal cords vibrate
- Guitar: strings vibrate
- Drum: skin vibrates

### Sound Needs a Medium
- Sound cannot travel through a vacuum (empty space)
- Sound travels fastest in solids, slowest in gases
- Speed in air ≈ 330 m/s

### Pitch and Frequency
- **High pitch** = High frequency (many vibrations/second)
- **Low pitch** = Low frequency (few vibrations/second)
- Shorter strings/columns = higher pitch

### Loudness and Amplitude
- **Loud** = Large amplitude (big vibrations)
- **Quiet** = Small amplitude (small vibrations)

### The Human Ear
1. Sound enters ear canal
2. Eardrum vibrates
3. Small bones amplify vibration
4. Cochlea converts to nerve signals
5. Brain interprets sound

### Echoes
- Sound reflecting off surfaces
- Used by bats and dolphins (echolocation)
- Used in sonar and ultrasound`,
          practiceQuestions: [
            "Why can't sound travel in space?",
            "What happens to pitch when you shorten a guitar string?",
            "What is the approximate speed of sound in air?",
            "How do bats use echoes to find food?",
            "Draw and label the main parts of the human ear"
          ],
          examTips: "Remember: pitch relates to frequency, loudness relates to amplitude."
        }
      ]
    },
    strand2: {
      name: "Materials",
      units: [
        {
          id: "8.4",
          name: "Materials",
          objectives: [
            "Classify materials as natural or man-made",
            "Understand properties of metals and non-metals",
            "Calculate and measure density",
            "Distinguish elements, compounds, and mixtures"
          ],
          keyTerms: [
            { term: "Density", definition: "Mass per unit volume (density = mass ÷ volume), measured in g/cm³" },
            { term: "Element", definition: "A pure substance made of only one type of atom" },
            { term: "Compound", definition: "A pure substance made of two or more elements chemically combined" },
            { term: "Mixture", definition: "Two or more substances mixed together but not chemically combined" },
            { term: "Metal", definition: "An element that is shiny, conducts electricity and heat, and is malleable" },
            { term: "Non-metal", definition: "An element that is dull, poor conductor, and often brittle" }
          ],
          studyNotes: `## Types of Materials

### Natural vs Man-made
| Natural | Man-made |
|---------|----------|
| Wood | Plastic |
| Cotton | Nylon |
| Stone | Concrete |
| Rubber | Glass |

### Metals vs Non-metals
| Property | Metals | Non-metals |
|----------|--------|------------|
| Appearance | Shiny | Dull |
| Conductivity | Good | Poor |
| Malleability | Can be hammered | Brittle |
| State | Mostly solid | Solid, liquid, gas |

### Density
**Formula: Density = Mass ÷ Volume**

Example: A block has mass 200g and volume 50cm³
Density = 200 ÷ 50 = 4 g/cm³

**Floating and Sinking:**
- Objects float if less dense than water (density < 1 g/cm³)
- Objects sink if more dense than water (density > 1 g/cm³)

### Elements, Compounds, Mixtures
| Type | Description | Examples |
|------|-------------|----------|
| Element | One type of atom | Iron, oxygen, carbon |
| Compound | Atoms chemically joined | Water (H₂O), salt (NaCl) |
| Mixture | Not chemically joined | Salt water, air |`,
          practiceQuestions: [
            "Calculate the density of an object with mass 150g and volume 30cm³",
            "Why does ice float on water?",
            "Give three properties of metals",
            "Is salt water an element, compound, or mixture? Explain.",
            "Name three man-made materials and their uses"
          ],
          examTips: "Always show the formula and working when calculating density. Include units in your answer."
        }
      ]
    },
    strand3: {
      name: "Ecology",
      units: [
        {
          id: "8.5",
          name: "Ecology",
          objectives: [
            "Understand ecosystems, communities, and habitats",
            "Identify food chains and food webs",
            "Explain photosynthesis and respiration",
            "Understand nutrient cycling"
          ],
          keyTerms: [
            { term: "Ecosystem", definition: "All living things and non-living factors in an area interacting together" },
            { term: "Community", definition: "All the different species living in an area" },
            { term: "Photosynthesis", definition: "The process by which plants make food using light energy" },
            { term: "Producer", definition: "An organism that makes its own food (autotroph)" },
            { term: "Consumer", definition: "An organism that eats other organisms (heterotroph)" },
            { term: "Decomposer", definition: "An organism that breaks down dead material (fungi, bacteria)" }
          ],
          studyNotes: `## Ecology

### Levels of Organization
1. **Individual** → 2. **Population** → 3. **Community** → 4. **Ecosystem**

### Food Chains and Webs
- **Producer** (plants) → **Primary consumer** (herbivore) → **Secondary consumer** (carnivore) → **Tertiary consumer** (top predator)
- Food webs show multiple interconnected chains

### Photosynthesis
**Equation:**
Carbon dioxide + Water → Glucose + Oxygen
(needs light and chlorophyll)

**Word equation:**
6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂

### Respiration
**Equation:**
Glucose + Oxygen → Carbon dioxide + Water + Energy

**All living things respire continuously**

### Nutrient Cycles
**Carbon Cycle:**
- Plants take in CO₂ (photosynthesis)
- Animals breathe out CO₂ (respiration)
- Decomposers release CO₂

**Nitrogen Cycle:**
- Plants need nitrogen for protein
- Bacteria fix nitrogen from air
- Decomposers return nitrogen to soil`,
          practiceQuestions: [
            "Write the word equation for photosynthesis",
            "Why are decomposers important in an ecosystem?",
            "Draw a food web for a coral reef ecosystem",
            "Explain how carbon is recycled in nature",
            "What is the difference between a community and an ecosystem?"
          ],
          examTips: "Be able to draw and interpret food chains and webs. Know the equations for photosynthesis and respiration."
        }
      ]
    }
  }
};

export const year9ScienceStudentNotes = {
  year: 9,
  subject: "Basic Science",
  strands: {
    strand1: {
      name: "Physics",
      units: [
        {
          id: "9.1",
          name: "Forces (1)",
          objectives: [
            "Understand force as a push or pull",
            "Measure forces in Newtons",
            "Understand friction and its effects",
            "Know about simple machines"
          ],
          keyTerms: [
            { term: "Force", definition: "A push or pull that can change an object's motion or shape" },
            { term: "Newton (N)", definition: "The unit of force - approximately the weight of a 100g mass" },
            { term: "Friction", definition: "A force that opposes motion between surfaces in contact" },
            { term: "Gravity", definition: "The force of attraction between masses - pulls objects toward Earth" },
            { term: "Mass", definition: "The amount of matter in an object, measured in kg" },
            { term: "Weight", definition: "The force of gravity acting on a mass, measured in Newtons" }
          ],
          studyNotes: `## Forces

### Types of Forces
- **Contact forces**: Friction, tension, normal force
- **Non-contact forces**: Gravity, magnetism, electrostatic

### Measuring Forces
- Measured using a spring balance (Newton meter)
- Unit: Newton (N)
- 1 N ≈ weight of 100g mass

### Friction
- Opposes motion between surfaces
- Can be useful (walking, brakes) or unwanted (wears machines)
- Reduced by: lubricants, smooth surfaces, ball bearings

### Mass vs Weight
| Mass | Weight |
|------|--------|
| Amount of matter | Force of gravity |
| Measured in kg | Measured in N |
| Same everywhere | Changes with gravity |

**Formula: Weight = Mass × Gravitational field strength**
On Earth: Weight = Mass × 10 (approximately)

### Simple Machines
- **Lever**: Pivots to multiply force
- **Pulley**: Changes direction of force
- **Inclined plane**: Reduces force needed
- **Wheel and axle**: Transfers force over distance`,
          practiceQuestions: [
            "What is the weight of a 5 kg mass on Earth?",
            "Name three ways to reduce friction",
            "Why does an astronaut weigh less on the Moon?",
            "Draw a lever and label the effort, load, and pivot",
            "Give two examples where friction is useful"
          ],
          examTips: "Remember: mass stays the same, weight changes with gravity. Weight = mass × 10 on Earth."
        },
        {
          id: "9.2",
          name: "Energy",
          objectives: [
            "Define energy and list its forms",
            "Describe energy transformations",
            "Understand conservation of energy",
            "Know about renewable and non-renewable energy"
          ],
          keyTerms: [
            { term: "Energy", definition: "The ability to do work or cause change, measured in Joules (J)" },
            { term: "Kinetic energy", definition: "Energy of moving objects" },
            { term: "Potential energy", definition: "Stored energy due to position or condition" },
            { term: "Conservation of energy", definition: "Energy cannot be created or destroyed, only transformed" },
            { term: "Renewable energy", definition: "Energy from sources that don't run out (solar, wind)" },
            { term: "Non-renewable energy", definition: "Energy from sources that will run out (fossil fuels)" }
          ],
          studyNotes: `## Energy

### Forms of Energy
1. **Kinetic** - moving objects
2. **Potential** - stored (gravitational, elastic, chemical)
3. **Thermal** - heat
4. **Light** - electromagnetic radiation
5. **Sound** - vibrations
6. **Electrical** - moving charges
7. **Nuclear** - from atom nuclei

### Energy Transformations
Energy changes from one form to another:
- Light bulb: Electrical → Light + Heat
- Car engine: Chemical → Kinetic + Heat
- Solar panel: Light → Electrical

### Conservation of Energy
**Energy cannot be created or destroyed**
- Total energy before = Total energy after
- Some energy is always "lost" as heat (wasted)

### Energy Sources
| Renewable | Non-renewable |
|-----------|---------------|
| Solar | Coal |
| Wind | Oil |
| Hydro | Natural gas |
| Geothermal | Nuclear |

### Efficiency
Not all energy is useful. Some is wasted as heat.
**Efficiency = (Useful energy out ÷ Total energy in) × 100%**`,
          practiceQuestions: [
            "Name the seven forms of energy",
            "Describe the energy transformations in a torch (flashlight)",
            "What does conservation of energy mean?",
            "Why are fossil fuels called non-renewable?",
            "Name three renewable energy sources suitable for Vanuatu"
          ],
          examTips: "Always trace the energy transformations step by step. Remember some energy is always wasted as heat."
        }
      ]
    },
    strand2: {
      name: "Chemistry",
      units: [
        {
          id: "9.3",
          name: "Particles (1)",
          objectives: [
            "Know matter is made of particles",
            "Understand the kinetic theory",
            "Know about atoms and molecules",
            "Use chemical symbols and formulae"
          ],
          keyTerms: [
            { term: "Atom", definition: "The smallest particle of an element that still has its properties" },
            { term: "Molecule", definition: "Two or more atoms joined together" },
            { term: "Kinetic theory", definition: "The idea that all matter is made of moving particles" },
            { term: "Diffusion", definition: "The spreading out of particles from high to low concentration" },
            { term: "Symbol", definition: "A one or two letter abbreviation for an element (e.g., O, Fe)" },
            { term: "Formula", definition: "Shows the atoms in a compound using symbols and numbers" }
          ],
          studyNotes: `## Particles

### Kinetic Theory
All matter is made of tiny particles that:
- Are constantly moving
- Have spaces between them
- Move faster when heated

### States of Matter (Particle Model)
| State | Arrangement | Movement | Spacing |
|-------|-------------|----------|---------|
| Solid | Regular pattern | Vibrate in place | Close |
| Liquid | Random | Slide past each other | Close |
| Gas | Random | Move freely, fast | Far apart |

### Atoms and Molecules
- **Atom**: Smallest particle of an element
- **Molecule**: Two or more atoms bonded together
- Same atoms = Element (O₂)
- Different atoms = Compound (H₂O)

### Chemical Symbols
| Element | Symbol |
|---------|--------|
| Hydrogen | H |
| Oxygen | O |
| Carbon | C |
| Nitrogen | N |
| Iron | Fe |
| Copper | Cu |

### Chemical Formulae
- H₂O = 2 hydrogen + 1 oxygen (water)
- CO₂ = 1 carbon + 2 oxygen (carbon dioxide)
- NaCl = 1 sodium + 1 chlorine (salt)`,
          practiceQuestions: [
            "Use kinetic theory to explain why gases can be compressed but liquids cannot",
            "What is the difference between an atom and a molecule?",
            "Write the formula for water and name the elements in it",
            "Explain diffusion using the particle model",
            "How do particles behave differently in solids, liquids, and gases?"
          ],
          examTips: "Draw particle diagrams to explain changes of state and diffusion."
        },
        {
          id: "9.5",
          name: "Acids, Bases and Salts",
          objectives: [
            "Identify acids, bases, and alkalis",
            "Use indicators and the pH scale",
            "Understand neutralisation reactions",
            "Know how salts are formed"
          ],
          keyTerms: [
            { term: "Acid", definition: "A substance with pH less than 7, produces H⁺ ions in water" },
            { term: "Base", definition: "A substance that neutralises an acid" },
            { term: "Alkali", definition: "A base that dissolves in water, pH greater than 7" },
            { term: "Indicator", definition: "A substance that changes color to show if something is acidic or alkaline" },
            { term: "pH scale", definition: "A scale from 0-14 measuring how acidic or alkaline a substance is" },
            { term: "Neutralisation", definition: "The reaction between an acid and base to form salt and water" }
          ],
          studyNotes: `## Acids, Bases and Salts

### The pH Scale
0 ←——— 7 ———→ 14
Acidic — Neutral — Alkaline

### Common Acids and Alkalis
| Acids | Alkalis |
|-------|---------|
| Hydrochloric acid (HCl) | Sodium hydroxide (NaOH) |
| Sulfuric acid (H₂SO₄) | Potassium hydroxide (KOH) |
| Vinegar (acetic acid) | Ammonia |
| Lemon juice (citric acid) | Baking soda solution |

### Indicators
| Indicator | In Acid | In Neutral | In Alkali |
|-----------|---------|------------|-----------|
| Litmus | Red | Purple | Blue |
| Universal indicator | Red/Orange | Green | Blue/Purple |
| Phenolphthalein | Colorless | Colorless | Pink |

### Neutralisation
**Acid + Base → Salt + Water**

Examples:
- HCl + NaOH → NaCl + H₂O
- H₂SO₄ + CuO → CuSO₄ + H₂O

### Acids with Carbonates
**Acid + Carbonate → Salt + Water + Carbon dioxide**
- This produces fizzing (effervescence)`,
          practiceQuestions: [
            "What is the pH of a neutral solution?",
            "Name three common acids found in the kitchen",
            "What color does universal indicator turn in acid?",
            "Write a word equation for the neutralisation of HCl with NaOH",
            "How would you test if a gas is carbon dioxide?"
          ],
          examTips: "Know the color changes of indicators. Be able to write word equations for neutralisation."
        }
      ]
    },
    strand3: {
      name: "Earth & Life Science",
      units: [
        {
          id: "9.6",
          name: "Earth Science",
          objectives: [
            "Understand the structure of the solar system",
            "Explain day, night, seasons, and tides",
            "Know Earth's internal structure",
            "Understand plate tectonics and its effects"
          ],
          keyTerms: [
            { term: "Plate tectonics", definition: "The theory that Earth's crust is divided into moving plates" },
            { term: "Volcano", definition: "An opening where molten rock (magma) reaches the surface" },
            { term: "Earthquake", definition: "Shaking of the ground caused by sudden movement of rock" },
            { term: "Orbit", definition: "The path of a planet or moon around another object" },
            { term: "Seismic waves", definition: "Vibrations that travel through Earth during an earthquake" }
          ],
          studyNotes: `## Earth Science

### The Solar System
- Sun at center
- 8 planets in orbit (Mercury, Venus, Earth, Mars, Jupiter, Saturn, Uranus, Neptune)
- Gravity keeps planets in orbit

### Earth's Movements
| Movement | Effect |
|----------|--------|
| Rotation (24 hrs) | Day and night |
| Revolution (365 days) | Seasons, year |
| Moon's orbit (28 days) | Lunar phases, tides |

### Earth's Structure
1. **Crust**: Thin outer layer (5-70 km)
2. **Mantle**: Hot, semi-solid (2900 km)
3. **Outer core**: Liquid iron (2200 km)
4. **Inner core**: Solid iron (1200 km)

### Plate Tectonics
- Earth's crust divided into plates
- Plates float on mantle
- Movement causes earthquakes and volcanoes

### Vanuatu's Position
- Located on Pacific Ring of Fire
- At boundary of Pacific and Australian plates
- High volcanic and earthquake activity
- Islands formed by volcanic eruptions`,
          practiceQuestions: [
            "Name the four layers of the Earth",
            "Why does Vanuatu have many volcanoes and earthquakes?",
            "Explain what causes day and night",
            "What is the Ring of Fire?",
            "Draw and label a cross-section of the Earth"
          ],
          examTips: "Know Vanuatu's geological position and why it has volcanic activity."
        },
        {
          id: "9.7",
          name: "The Human Body",
          objectives: [
            "Know the organization of the human body",
            "Understand the circulatory system",
            "Know the structure and function of blood",
            "Understand the respiratory and digestive systems"
          ],
          keyTerms: [
            { term: "Organ", definition: "A group of tissues working together for a specific function" },
            { term: "System", definition: "A group of organs working together for a major function" },
            { term: "Artery", definition: "A blood vessel carrying blood away from the heart" },
            { term: "Vein", definition: "A blood vessel carrying blood toward the heart" },
            { term: "Capillary", definition: "A tiny blood vessel where exchange of materials occurs" },
            { term: "Haemoglobin", definition: "The protein in red blood cells that carries oxygen" }
          ],
          studyNotes: `## Human Body Organization

### Levels of Organization
Cell → Tissue → Organ → System → Organism

### Circulatory System
**Heart**: Pumps blood (4 chambers)
**Blood vessels**:
- Arteries: Thick walls, carry blood FROM heart
- Veins: Thinner walls, carry blood TO heart
- Capillaries: Tiny, thin walls for exchange

**Blood composition**:
- Red cells: Carry oxygen (haemoglobin)
- White cells: Fight infection
- Platelets: Help clotting
- Plasma: Liquid, carries dissolved substances

### Respiratory System
- Nose/mouth → Trachea → Bronchi → Bronchioles → Alveoli
- Gas exchange in alveoli
- Oxygen in, carbon dioxide out

### Digestive System
- Mouth (mechanical + saliva)
- Stomach (acid + enzymes)
- Small intestine (digestion + absorption)
- Large intestine (water absorption)`,
          practiceQuestions: [
            "List the levels of organization from cell to organism",
            "What is the difference between arteries and veins?",
            "What is the function of red blood cells?",
            "Where does gas exchange occur in the lungs?",
            "Name three components of blood and their functions"
          ],
          examTips: "Be able to trace the path of blood through the heart and body."
        }
      ]
    }
  }
};

export const year10ScienceStudentNotes = {
  year: 10,
  subject: "Basic Science",
  strands: {
    strand1: {
      name: "Physics & Chemistry",
      units: [
        {
          id: "10.1",
          name: "Forces (2)",
          objectives: [
            "Calculate moments of force",
            "Solve problems using principle of moments",
            "Understand pressure and its applications",
            "Calculate pressure in different situations"
          ],
          keyTerms: [
            { term: "Moment", definition: "The turning effect of a force = Force × Perpendicular distance from pivot" },
            { term: "Equilibrium", definition: "When the clockwise moments equal the anticlockwise moments" },
            { term: "Pressure", definition: "Force per unit area = Force ÷ Area, measured in Pa (N/m²)" },
            { term: "Pascal (Pa)", definition: "The unit of pressure, equal to 1 N/m²" },
            { term: "Acceleration", definition: "The rate of change of velocity" }
          ],
          studyNotes: `## Forces (2)

### Moments
**Moment = Force × Distance from pivot**

Units: Newton-meters (Nm)

### Principle of Moments
For balance (equilibrium):
**Clockwise moments = Anticlockwise moments**

Example: Seesaw
If 400N child sits 2m from pivot:
Moment = 400 × 2 = 800 Nm
For balance, other side needs 800 Nm too
(e.g., 200N child at 4m: 200 × 4 = 800 Nm)

### Pressure
**Pressure = Force ÷ Area**

Units: Pascals (Pa) or N/m²

Examples:
- Sharp knife: small area = high pressure = cuts easily
- Snowshoes: large area = low pressure = don't sink

### Fluid Pressure
- Increases with depth
- Acts in all directions
- Water pressure = ρgh (density × gravity × height)

### Applications
- Hydraulic machines (force multiplied)
- Atmospheric pressure (weight of air)
- Blood pressure (measured in mmHg)`,
          practiceQuestions: [
            "Calculate the moment of a 50N force acting 0.4m from the pivot",
            "A seesaw has a 600N person 2m from the pivot. Where should a 400N person sit to balance?",
            "Calculate the pressure under a box of weight 200N with base area 0.5m²",
            "Why do tractors have large tires?",
            "Explain why water pressure increases with depth"
          ],
          examTips: "Always show the formula, substitution, and units in calculations. Draw diagrams for moment problems."
        },
        {
          id: "10.3",
          name: "Electricity",
          objectives: [
            "Understand static electricity",
            "Build and analyze series and parallel circuits",
            "Understand current, voltage, and resistance",
            "Calculate electrical power and energy"
          ],
          keyTerms: [
            { term: "Current (I)", definition: "The flow of electric charge, measured in Amperes (A)" },
            { term: "Voltage (V)", definition: "The electrical 'push' or potential difference, measured in Volts" },
            { term: "Resistance (R)", definition: "Opposition to current flow, measured in Ohms (Ω)" },
            { term: "Power (P)", definition: "Rate of energy transfer = V × I, measured in Watts (W)" },
            { term: "Series circuit", definition: "Components connected in a single loop - current is same throughout" },
            { term: "Parallel circuit", definition: "Components on separate branches - voltage is same across each" }
          ],
          studyNotes: `## Electricity

### Static Electricity
- Objects become charged by gaining or losing electrons
- Like charges repel, unlike charges attract
- Lightning is a static discharge

### Circuits
**Series Circuit:**
- One path for current
- Current same throughout: I₁ = I₂ = I₃
- Voltages add up: V = V₁ + V₂ + V₃

**Parallel Circuit:**
- Multiple paths for current
- Voltage same across each branch: V₁ = V₂ = V₃
- Currents add up: I = I₁ + I₂ + I₃

### Ohm's Law
**V = I × R**
- Voltage = Current × Resistance

### Power and Energy
**Power = Voltage × Current (P = V × I)**
Units: Watts (W)

**Energy = Power × Time (E = P × t)**
Units: Joules (J) or kilowatt-hours (kWh)

### Domestic Electricity
- Main supply: 240V AC
- Fuses protect circuits
- Circuit breakers cut off current
- Earthing prevents shocks`,
          practiceQuestions: [
            "Calculate the current in a circuit with voltage 12V and resistance 4Ω",
            "A bulb uses 60W of power at 240V. What current flows through it?",
            "Draw a circuit with two bulbs in parallel with one cell",
            "A 2kW heater runs for 3 hours. How much energy does it use in kWh?",
            "Why are household circuits wired in parallel, not series?"
          ],
          examTips: "Know the formulas V=IR and P=VI. Practice drawing circuit diagrams with correct symbols."
        }
      ]
    },
    strand2: {
      name: "Biology",
      units: [
        {
          id: "10.4",
          name: "Health and Hygiene",
          objectives: [
            "Know causes and transmission of common diseases",
            "Understand the body's defense mechanisms",
            "Know about immunity and vaccination",
            "Understand the importance of hygiene"
          ],
          keyTerms: [
            { term: "Pathogen", definition: "A microorganism that causes disease (bacteria, virus, fungus)" },
            { term: "Immunity", definition: "The body's ability to resist a particular disease" },
            { term: "Antibody", definition: "A protein made by white blood cells to fight specific pathogens" },
            { term: "Vaccination", definition: "Introducing weakened pathogen to trigger immunity without disease" },
            { term: "Antibiotic", definition: "A medicine that kills bacteria (not viruses)" },
            { term: "STD/STI", definition: "Sexually transmitted disease/infection" }
          ],
          studyNotes: `## Health and Hygiene

### Disease Transmission
| Method | Examples |
|--------|----------|
| Airborne | Flu, COVID, TB |
| Waterborne | Cholera, typhoid |
| Contact | Skin infections |
| Vectors (insects) | Malaria, dengue |
| Sexual contact | HIV, gonorrhea |

### Body Defenses
**First line (physical barriers):**
- Skin, mucus, stomach acid, tears

**Second line (non-specific):**
- White blood cells engulf pathogens
- Fever kills pathogens
- Inflammation

**Third line (specific/immune):**
- Lymphocytes make antibodies
- Memory cells remember pathogens

### Immunity Types
- **Natural**: From having the disease
- **Artificial**: From vaccination

### Vaccination
1. Weakened/dead pathogen injected
2. Body makes antibodies
3. Memory cells formed
4. Future infection fought quickly

### HIV/AIDS
- HIV attacks immune system
- Transmitted: Blood, sexual contact, mother to child
- No cure, but treatments available
- Prevention: Safe practices, education`,
          practiceQuestions: [
            "Name three ways diseases can be transmitted",
            "What is the difference between an antibiotic and a vaccine?",
            "Explain how vaccination works",
            "Why can't antibiotics treat viral infections?",
            "List three ways to prevent the spread of infectious diseases"
          ],
          examTips: "Know the difference between natural and artificial immunity. Understand how vaccines work."
        },
        {
          id: "10.5",
          name: "The Biosphere",
          objectives: [
            "Understand ecosystems and energy flow",
            "Explain nutrient cycling",
            "Know about human impact on the environment",
            "Understand conservation strategies"
          ],
          keyTerms: [
            { term: "Biosphere", definition: "All parts of Earth where life exists" },
            { term: "Biome", definition: "A large ecosystem with similar climate and organisms" },
            { term: "Food web", definition: "Interconnected food chains in an ecosystem" },
            { term: "Biomass pyramid", definition: "Shows mass of organisms at each feeding level" },
            { term: "Conservation", definition: "Protection and careful management of natural resources" },
            { term: "Sustainable development", definition: "Meeting present needs without compromising future generations" }
          ],
          studyNotes: `## The Biosphere

### Ecosystems
Components:
- **Biotic**: Living things (plants, animals, bacteria)
- **Abiotic**: Non-living things (water, temperature, soil)

### Energy Flow
Sun → Producers → Primary consumers → Secondary consumers → Decomposers

**Energy pyramid**: Only ~10% energy passes to next level

### Nutrient Cycles
**Carbon cycle**: Photosynthesis, respiration, decomposition, combustion
**Nitrogen cycle**: Fixation, nitrification, denitrification

### Human Impact
| Problem | Cause | Effect |
|---------|-------|--------|
| Deforestation | Clearing forests | Habitat loss, erosion |
| Pollution | Industry, waste | Water/air contamination |
| Overfishing | Excessive harvesting | Population decline |
| Climate change | Fossil fuels | Rising temperatures |

### Conservation in Vanuatu
- Marine protected areas
- Forest reserves
- Sustainable fishing practices
- Traditional knowledge (tabu system)
- Reef protection from crown-of-thorns starfish

### Solutions
- Protected areas
- Sustainable harvesting
- Replanting programs
- Reducing pollution
- Education and awareness`,
          practiceQuestions: [
            "What is the difference between a biome and an ecosystem?",
            "Why is only 10% of energy passed to the next trophic level?",
            "Explain one way humans are affecting the environment in Vanuatu",
            "What is meant by sustainable development?",
            "Describe two conservation strategies used in Vanuatu"
          ],
          examTips: "Know examples of environmental issues in Vanuatu and conservation strategies being used."
        }
      ]
    }
  }
};

export default {
  year7ScienceStudentNotes,
  year8ScienceStudentNotes,
  year9ScienceStudentNotes,
  year10ScienceStudentNotes
};
